import React, { useState } from "react";

// CVSS v3.1 / v4.0 score+vector display, plus a simple illustrative
// calculator (not a certified CVSS engine — for UI/demo purposes only).
const CVSS3_METRICS = [
  { key: "AV", label: "Attack Vector", options: ["N", "A", "L", "P"] },
  { key: "AC", label: "Attack Complexity", options: ["L", "H"] },
  { key: "PR", label: "Privileges Required", options: ["N", "L", "H"] },
  { key: "UI", label: "User Interaction", options: ["N", "R"] },
  { key: "C", label: "Confidentiality", options: ["N", "L", "H"] },
  { key: "I", label: "Integrity", options: ["N", "L", "H"] },
  { key: "A", label: "Availability", options: ["N", "L", "H"] },
];

const WEIGHT = { N: 0, L: 0.4, H: 1, A: 0, P: 0.2, R: 0.5 };

const estimateScore = (values) => {
  // Simplified illustrative scoring — not the official CVSS formula.
  const impactKeys = ["C", "I", "A"];
  const impact = impactKeys.reduce((sum, k) => sum + (WEIGHT[values[k]] ?? 0), 0);
  const exploitability =
    (values.AV === "N" ? 0.85 : values.AV === "A" ? 0.62 : values.AV === "L" ? 0.55 : 0.2) +
    (values.AC === "L" ? 0.77 : 0.44) +
    (values.PR === "N" ? 0.85 : values.PR === "L" ? 0.62 : 0.27) +
    (values.UI === "N" ? 0.85 : 0.62);
  const raw = Math.min(10, ((impact / 3) * 6 + (exploitability / 4) * 4));
  return Math.round(raw * 10) / 10;
};

const VaptCvssPanel = ({ cvssV3, cvssV4, editable = false, onChange }) => {
  const [values, setValues] = useState({ AV: "N", AC: "L", PR: "N", UI: "N", C: "H", I: "H", A: "H" });
  const [showCalculator, setShowCalculator] = useState(false);

  const setMetric = (key, val) => {
    const next = { ...values, [key]: val };
    setValues(next);
    if (onChange) {
      const vector = `AV:${next.AV}/AC:${next.AC}/PR:${next.PR}/UI:${next.UI}/S:U/C:${next.C}/I:${next.I}/A:${next.A}`;
      onChange({ score: estimateScore(next).toFixed(1), vector });
    }
  };

  const scoreColor = (score) => {
    const s = Number(score);
    if (s >= 9) return "text-red-600 bg-red-50 border-red-200";
    if (s >= 7) return "text-orange-600 bg-orange-50 border-orange-200";
    if (s >= 4) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-green-600 bg-green-50 border-green-200";
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className={`rounded-xl border p-4 ${scoreColor(cvssV3?.score)}`}>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-80">CVSS v3.1</p>
          <p className="text-3xl font-bold">{cvssV3?.score ?? "-"}</p>
          <p className="text-[11px] font-mono break-all opacity-80 mt-1">{cvssV3?.vector}</p>
        </div>
        <div className={`rounded-xl border p-4 ${scoreColor(cvssV4?.score)}`}>
          <p className="text-xs font-semibold uppercase tracking-wide opacity-80">CVSS v4.0</p>
          <p className="text-3xl font-bold">{cvssV4?.score ?? "-"}</p>
          <p className="text-[11px] font-mono break-all opacity-80 mt-1">{cvssV4?.vector}</p>
        </div>
      </div>

      {editable && (
        <div>
          <button
            type="button"
            onClick={() => setShowCalculator((s) => !s)}
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            {showCalculator ? "Hide" : "Open"} simple CVSS calculator
          </button>

          {showCalculator && (
            <div className="mt-3 border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {CVSS3_METRICS.map((m) => (
                  <div key={m.key}>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">{m.label}</label>
                    <select
                      value={values[m.key]}
                      onChange={(e) => setMetric(m.key, e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                    >
                      {m.options.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                Estimated score: <span className="font-bold text-gray-800">{estimateScore(values).toFixed(1)}</span>{" "}
                (illustrative calculation, not an official CVSS implementation)
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VaptCvssPanel;
